import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { UserConnectionsSkeleton } from '../components/Skeletons';
import config from '../config';
import '../styles/userConnections.css';
import formatUrl from '../utils/urlFormatter';
import ScrollToTopButton from '../components/ScrollToTopButton';
import debounce from 'lodash.debounce';

const UserConnections = () => {
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searching, setSearching] = useState(false);
    const { userId, type } = useParams();
    const navigate = useNavigate();

    // Fetch users on component mount
    useEffect(() => {
        const token = Cookies.get('authToken');
        if (!token) {
            navigate('/login', { state: { from: `/users/${userId}/${type}` } });
            return;
        }
        fetchUsers();
    }, [userId, type, navigate]);

    // Debounced search function
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSearch = useCallback(
        debounce((query) => {
            setSearching(true);
            if (!query.trim()) {
                setFilteredUsers(users);
                setSearching(false);
                return;
            }

            const q = query.toLowerCase();
            const filtered = users.filter(user =>
                user.username.toLowerCase().includes(q) ||
                user.first_name.toLowerCase().includes(q) ||
                user.last_name.toLowerCase().includes(q)
            );
            setFilteredUsers(filtered);
            setSearching(false);
        }, 300),
        [users]
    );

    // Filter users based on search query
    useEffect(() => {
        debouncedSearch(searchQuery);
    }, [searchQuery, debouncedSearch]);

    // Fetch users from the API
    const fetchUsers = async () => {
        try {
            const token = Cookies.get('authToken');
            if (!token) {
                throw new Error('No authentication token found');
            }

            const endpoint = type === 'followers'
                ? `${config.backendUrl}/users/followers/${userId}/`
                : `${config.backendUrl}/users/following/${userId}/`;

            const response = await fetch(endpoint, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${token}`
                }
            });

            if (response.status === 401) {
                navigate('/login', { state: { from: `/users/${userId}/${type}` } });
                return;
            }

            if (!response.ok) {
                throw new Error('Failed to fetch users');
            }

            const data = await response.json();
            setUsers(data.results || data);
            setFilteredUsers(data.results || data);
            setError(null);
        } catch (error) {
            console.error('Error fetching users:', error);
            setError('Une erreur est survenue lors du chargement des utilisateurs');
        } finally {
            setLoading(false);
        }
    };

    // Handle search input
    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    // Clear search
    const handleClearSearch = () => {
        setSearchQuery('');
        setFilteredUsers(users);
    };

    // Get user initials
    const getUserInitials = (firstName, lastName) => {
        return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || '?';
    };

    // Handle user click
    const handleUserClick = (user) => {
        const userIdentifier = user.uuid || user.id;
        navigate(`/user/corner/${userIdentifier}/`);
    };

    // Handle image error
    const handleImageError = (e) => {
        e.target.style.display = 'none';
        e.target.nextElementSibling.style.display = 'flex';
    };

    return (
        < div className="user-connections-page" >
            <div className="connections-header">
                <h1 className="connections-title">
                    {type === 'followers' ? 'Mes Clients' : 'Mes Revendeurs'}
                </h1>
                <p className="connections-subtitle">
                    {type === 'followers'
                        ? 'Les personnes qui suivent vos produits'
                        : 'Les revendeurs que vous suivez'}
                </p>
            </div>

            <div className="search-container">
                <div className="search-input-wrapper">
                    <input
                        type="text"
                        placeholder="Rechercher un utilisateur..."
                        value={searchQuery}
                        onChange={handleSearch}
                        className="search-input"
                    />
                    {searchQuery && (
                        <button
                            className="clear-search-button"
                            onClick={handleClearSearch}
                            aria-label="Clear search"
                        >
                            <i className="fas fa-times"></i>
                        </button>
                    )}
                </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            {
                loading ? (
                    <UserConnectionsSkeleton />
                ) : (
                    <div className="connections-list">
                        {filteredUsers.length > 0 ? (
                            filteredUsers.map(user => (
                                <div
                                    key={user.id}
                                    className="connection-card"
                                    onClick={() => handleUserClick(user)}
                                >
                                    <div className="connection-avatar-container">
                                        {(user.profile_picture || user.profile_picture_url) && (
                                            <img
                                                src={formatUrl(user.profile_picture || user.profile_picture_url)}
                                                alt={`${user.first_name} ${user.last_name}`}
                                                className="user-avatar"
                                                onError={handleImageError}
                                            />
                                        )}
                                        <div className="user-initials" style={{
                                            display: (user.profile_picture || user.profile_picture_url) ? 'none' : 'flex'
                                        }}>
                                            {getUserInitials(user.first_name, user.last_name)}
                                        </div>
                                    </div>
                                    <div className="connection-details">
                                        <h3 className="connection-name">
                                            {user.first_name} {user.last_name}
                                        </h3>
                                        <p className="connection-username">@{user.username}</p>
                                    </div>
                                    <div className="connection-actions">
                                        <button
                                            className="view-profile-button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleUserClick(user);
                                            }}
                                        >
                                            <i className="fas fa-user-circle"></i>
                                            <span>Voir le profil</span>
                                        </button>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="empty-connections-card">
                                <div className="empty-connections-content">
                                    <div className="empty-icon">
                                        {searchQuery ? '🔍' : type === 'followers' ? '👥' : '🤝'}
                                    </div>
                                    <h2>
                                        {searchQuery
                                            ? 'Aucun résultat trouvé'
                                            : type === 'followers'
                                                ? 'Aucun client pour le moment'
                                                : 'Aucun revendeur pour le moment'}
                                    </h2>
                                    <p>
                                        {searchQuery
                                            ? 'Essayez d\'autres termes de recherche'
                                            : type === 'followers'
                                                ? 'Commencez à vendre des produits et attirez des clients!'
                                                : 'Découvrez des revendeurs de confiance et élargissez votre réseau.'}
                                    </p>
                                    {!searchQuery && (
                                        <button
                                            className="discover-users-button"
                                            onClick={() => navigate('/users/discover')}
                                        >
                                            <i className="fas fa-search"></i>
                                            <span>
                                                {type === 'followers'
                                                    ? 'Promouvoir Mes Produits'
                                                    : 'Découvrir des Revendeurs'}
                                            </span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )
            }
            <ScrollToTopButton />
        </div >
    );
};

export default UserConnections;